/**
 * AssignedTasksBell — campanellino Navbar per compiti assegnati a me.
 *
 * Comportamento:
 *  - All'apertura fetcha ``/admin/assigned-tasks`` e conta i compiti pending
 *    dove ``assignee_id == myId`` (esclude i "creati da me").
 *  - In real-time via ``AdminNotificationsContext``:
 *     - ``assigned_task.created`` assegnato a me → count+1 + toast + Notification
 *     - ``assigned_task.reopened`` assegnato a me → count+1
 *     - ``assigned_task.completed`` (assignee=me) → count-1
 *     - ``assigned_task.deleted`` → refetch (non sappiamo se era pending mio)
 *  - Click → naviga a /admin?tab=home (dove c'è il pannello Compiti Assegnati)
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CheckSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAdminNotificationEvents } from "../context/AdminNotificationsContext";
import { toast } from "sonner";

export default function AssignedTasksBell({ user }) {
  const [count, setCount] = useState(0);
  const [flash, setFlash] = useState(false);
  const navigate = useNavigate();
  const myId = user?.id;
  const isAdmin = !!user && user.role === "admin";
  const isVisibleRef = useRef(true);

  const recount = useCallback(async () => {
    if (!isAdmin || !myId) return;
    try {
      const r = await api.get("/admin/assigned-tasks");
      const n = (r.data || []).filter(
        (t) => t.status === "pending" && t.assignee_id === myId,
      ).length;
      setCount(n);
    } catch {
      /* silente */
    }
  }, [isAdmin, myId]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    recount();
    // Fallback: se SSE non funziona, refetcha ogni 90s
    const iv = setInterval(recount, 90000);
    const onFocus = () => recount();
    const onVis = () => { isVisibleRef.current = !document.hidden; };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [isAdmin, recount]);

  const triggerFlash = useCallback(() => {
    setFlash(true);
    setTimeout(() => setFlash(false), 1200);
  }, []);

  const showBrowserNotification = useCallback((title, body) => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    try {
      const n = new Notification(title, {
        body: (body || "").slice(0, 160),
        tag: "archihub-assigned-task-bell",
        renotify: true,
      });
      n.onclick = () => {
        try { window.focus(); } catch { /* ignore */ }
        n.close();
      };
    } catch { /* ignore */ }
  }, []);

  useAdminNotificationEvents("assigned_task.", (event) => {
    const type = event?.type;
    if (!type || !type.startsWith("assigned_task.")) return;

    if (type === "assigned_task.deleted") {
      // Non sappiamo il payload — safest è refetchare
      recount();
      return;
    }
    const t = event.task;
    if (!t) return;

    if (type === "assigned_task.created" && t.assignee_id === myId) {
      const madeByMe = t.creator_id === myId;
      if (madeByMe) return; // self-assigned → non è "novità da altri"
      setCount((c) => c + 1);
      triggerFlash();
      showBrowserNotification(`Nuovo compito da ${t.creator_name}`, t.description);
      if (isVisibleRef.current) toast.success(`Nuovo compito: ${t.description.slice(0, 60)}`);
    } else if (type === "assigned_task.reopened" && t.assignee_id === myId) {
      setCount((c) => c + 1);
      triggerFlash();
    } else if (type === "assigned_task.completed") {
      // Se sono io l'assignee e il task era pending, decremento
      if (t.assignee_id === myId) {
        setCount((c) => Math.max(0, c - 1));
      }
    }
  });

  useAdminNotificationEvents("installers.timbratura_far", (event) => {
    const km = event?.distance_km != null ? `${event.distance_km} km` : "oltre 2 km";
    const dir = event?.direction === "in" ? "entrata" : "uscita";
    const msg = `⚠ ${event?.worker_name || "Operaio"} ha timbrato ${dir === "entrata" ? "l'entrata" : "l'uscita"} a ${km} dal cantiere${event?.site_name ? ` ${event.site_name}` : ""}${event?.address ? ` — ${event.address}` : ""}`;
    toast.error(msg, { duration: 12000 });
    showBrowserNotification("Timbratura lontana dal cantiere", msg);
    triggerFlash();
  });

  useAdminNotificationEvents("installers.assistance_claimed", (event) => {
    const msg = `${event?.worker_name || "Un operaio"} ha preso in carico l'assistenza di ${event?.client_name || "un cliente"}${event?.client_address ? ` — ${event.client_address}` : ""}`;
    toast.success(msg, { duration: 10000 });
    showBrowserNotification("Assistenza presa in carico", msg);
    triggerFlash();
  });

  if (!isAdmin) return null;

  const has = count > 0;

  return (
    <button
      type="button"
      onClick={() => navigate("/admin?tab=home")}
      className={`relative inline-flex items-center justify-center w-9 h-9 rounded-sm hover:bg-cream2 transition-all cursor-pointer ${flash ? "ring-2 ring-gold ring-offset-1 animate-pulse" : ""}`}
      title={has ? `${count} ${count === 1 ? "compito assegnato" : "compiti assegnati"} da fare · clicca per aprirli` : "Nessun compito assegnato in attesa"}
      data-testid="assigned-tasks-bell"
      data-count={count}
      aria-label={has ? `${count} compiti in attesa` : "Compiti assegnati"}
    >
      <CheckSquare size={16} className={`pointer-events-none ${has ? "text-amber-800" : "text-slate1"}`} strokeWidth={2} />
      {has && (
        <span
          className="pointer-events-none absolute -top-0.5 -right-0.5 bg-gold text-ink text-[9px] font-semibold rounded-full min-w-[16px] h-4 px-1 inline-flex items-center justify-center animate-pulse"
          data-testid="assigned-tasks-bell-count"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}
